import {msg, str} from '@lit/localize';
import {html, LitElement} from 'lit';
import {isValidIBAN, electronicFormatIBAN} from 'ibantools';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {connect} from 'pwa-helpers';
import {navigateTo, openDialog} from '../../slices/statusSlice';
import {BILLING_ANNUALLY, BILLING_MONTHLY} from '../../services/subscription/billingCycle';
import {subscribeWithSEPA} from '../../slices/planSlice';
import {validateVatId} from '../../services/subscription/vatIdValidation';
import style from '../../../../scss/sepaCheckout.scss';
import {selectCurrentProfileId, selectLanguage} from '../../selectors';
import store from '../../store';
import {sepaCountriesSorted} from '../../services/subscription/sepaCountries';
import arrowRight from '../../icons/arrowRight';
import close from '../../icons/close';

@customElement('arc-sepa-checkout')
export class SEPACheckout extends connect(store)(LitElement) {
	static styles = [style];

	static properties = {
		formValues: {type: Object},
		lang: {type: String},
		onClose: {type: Function},
		subscribing: {type: Boolean},
		planName: {type: String},
		price: {type: Object},
		billingCycle: {type: String},
		show: {type: Boolean},
	};

	constructor() {
		super();
		this.inputErrors = {};
		this.subscribing = false;
		this.planName = 'AAKIO Artist';
		this.price = null;
		this.show = false;
		this.billingCycle = BILLING_MONTHLY;
		this.isValid = false;
		this.lang = 'de';
		this.onClose = () => null;
		this.blurredFields = new Set(); // Track which fields have been blurred
		this.countries = sepaCountriesSorted(this.lang);
		this.formValues = {
			email: '',
			name: '',
			street: '',
			city: '',
			zip: '',
			country: '',
			vatId: '',
			accountName: '',
			iban: '',
			tac: false,
		};
		if (document.location.href.includes('debug')) {
			this.formValues = {
				email: 'dev@example.com',
				name: 'dev',
				street: 'dev street 10',
				city: 'devtown',
				zip: '10000',
				country: 'DE',
				vatId: '',
				accountName: 'dev devton',
				iban: 'DE75512108001245126199',
				tac: true,
			};
		}

		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.lang = selectLanguage(state);
		this.countries = sepaCountriesSorted(this.lang);
		if (state.plan.loading === '' && this.subscribing) {
			this.onClose();
			this.subscribing = false;
			const dialogOptions = {
				title: msg('sepa_checkout_confirmation_title'),
				message: msg('sepa_checkout_confirmation_message'),
				labelCancel: msg('sepa_checkout_confirmation_action'),
				showConfirmButton: false,
			};
			this._handleCloseDialog = async () => {
				const profileId = selectCurrentProfileId(state);
				store.dispatch(navigateTo({route: 'artworks', params: {language: this.lang, id: profileId}}));
				window.removeEventListener('cancel', this._handleCloseDialog);
			};
			window.addEventListener('cancel', this._handleCloseDialog);
			store.dispatch(openDialog(dialogOptions));
		}
	}

	async onSubscribe() {
		if (!this.validateForm(true)) return;
		this.subscribing = true;
		await store.dispatch(
			subscribeWithSEPA({priceId: this.price.id, ...this.formValues})
		);
	}

	setFormValue(key, value) {
		this.formValues[key] = value;
		this.validateForm();
		this.requestUpdate();
	}

	onFieldBlur(field) {
		this.blurredFields.add(field);
		this.validateForm();
		this.requestUpdate();
	}

	render() {
		if (!this.show) return null;
		const planName = this.planName;
		const price = this.price.price / 100;
		const annualPrice = price * 12;
		const billingCycle = (this.billingCycle === BILLING_MONTHLY
			? msg('billing_cycle_monthly')
			: msg('billing_cycle_annually')).toLowerCase();
		let billingNote = '';
		if (this.billingCycle === BILLING_ANNUALLY) {
			billingNote = msg(str`${annualPrice}€ charged annually at the start of the year.`, {
				id: 'sepa_checkout_billing_note',
			});
		}
		return html`
			<div class="modal">
				<div class="modal-content">
					<div class="sepaCheckout">
						<h1 class="title">${msg('sepa_checkout_headline')}</h1>
						<p class="header">
							${msg(
								html`You have chosen <b>${planName}</b>. You will pay ${price}.${billingCycle}.${billingNote}.`,
								{id: 'sepa_checkout_details'}
							)}
							<br/>
							${msg('sepa_checkout_instructions')}
						</p>
						<div class="form-section">
							<div class="form-row">
								<arc-text-input
									title="${msg('sepa_checkout_email_label')}"
									required="true"
									type="email"
									placeholder="${msg('sepa_checkout_email_placeholder')}"
									.value=${this.formValues.email}
									.errors=${this.inputErrors.email}
									@value-change=${(e) => this.setFormValue('email', e.detail.value)}
									@blur=${() => this.onFieldBlur('email')}
								>
								</arc-text-input>
								<arc-text-input
									title="${msg('sepa_checkout_name_label')}"
									placeholder="${msg('sepa_checkout_name_placeholder')}"
									required="true"
									.value=${this.formValues.name}
									.errors=${this.inputErrors.name}
									@value-change=${(e) => this.setFormValue('name', e.detail.value)}
									@blur=${() => this.onFieldBlur('name')}
								>
								</arc-text-input>
							</div>
							<div class="form-row">
								<arc-text-input
									title="${msg('sepa_checkout_street_label')}"
									placeholder="${msg('sepa_checkout_street_placeholder')}"
									required="true"
									.value=${this.formValues.street}
									.errors=${this.inputErrors.street}
									@value-change=${(e) => this.setFormValue('street', e.detail.value)}
									@blur=${() => this.onFieldBlur('street')}
								>
								</arc-text-input>
								<arc-text-input
									title="${msg('sepa_checkout_city_label')}"
									placeholder="${msg('sepa_checkout_city_placeholder')}"
									required="true"
									.value=${this.formValues.city}
									.errors=${this.inputErrors.city}
									@value-change=${(e) => this.setFormValue('city', e.detail.value)}
									@blur=${() => this.onFieldBlur('city')}
								>
								</arc-text-input>
							</div>
							<div class="form-row">
								<arc-text-input
									title="${msg('sepa_checkout_zip_label')}"
									placeholder="${msg('sepa_checkout_zip_placeholder')}"
									required="true"
									.value=${this.formValues.zip}
									.errors=${this.inputErrors.zip}
									@value-change=${(e) => this.setFormValue('zip', e.detail.value)}
									@blur=${() => this.onFieldBlur('zip')}
								>
								</arc-text-input>
								<arc-select
									class="formInput"
									required="true"
									.emptyOption=${' '}
									title="${msg('sepa_checkout_country_label')}"
									.options=${this.countries.map((country) => ({
										id: country.code,
										name: [
											{lang: 'en', value: country.name.en},
											{lang: 'de', value: country.name.de},
										],
									}))}
									.value=${this.formValues.country}
									.errors=${this.inputErrors.country}
									@value-change=${(e) => this.setFormValue('country', e.detail.value)}
									@blur=${() => this.onFieldBlur('country')}
								></arc-select>
							</div>
							${when(
								this.formValues.country.length > 1 && this.formValues.country !== 'DE',
								() => html`
									<div class="form-row">
										<arc-text-input
											title="${msg('sepa_checkout_vatId_label')}"
											placeholder="${msg('sepa_checkout_vatId_placeholder')}"
											.value=${this.formValues.vatId}
											.errors=${this.inputErrors.vatId}
											@value-change=${(e) => this.setFormValue('vatId', e.detail.value)}
											@blur=${() => this.onFieldBlur('vatId')}
										>
										</arc-text-input>
										<div>&nbsp;</div>
									</div>
								`
							)}
							<hr />
							<div class="form-row">
								<arc-text-input
									title="${msg('sepa_checkout_accountName_label')}"
									placeholder="${msg('sepa_checkout_accountName_placeholder')}"
									required="true"
									.value=${this.formValues.accountName}
									.errors=${this.inputErrors.accountName}
									@value-change=${(e) => this.setFormValue('accountName', e.detail.value)}
									@blur=${() => this.onFieldBlur('accountName')}
								>
								</arc-text-input>
								<arc-text-input
									title="${msg('sepa_checkout_iban_label')}"
									placeholder="${msg('sepa_checkout_iban_placeholder')}"
									required="true"
									.value=${this.formValues.iban}
									.errors=${this.inputErrors.iban}
									.maxLength=${34}
									.maxLengthWarning=${false}
									@value-change=${(e) => {
										let v = e.detail.value;
										v = v
											.replace(/\s+/g, '')
											.replace(/([a-zA-Z]{2})/gi, '$1 ')
											.replace(/([0-9]{4})/gi, '$1 ')
											.trim()
											.toLocaleUpperCase();
										this.setFormValue('iban', v);
									}}
									@blur=${() => this.onFieldBlur('iban')}
								>
								</arc-text-input>
							</div>
							<div class="form-row">
								<arc-checkbox
									title="${msg('sepa_checkout_tac_label')}"
									.noFieldTitle=${true}
									.value=${this.formValues.tac}
									.errors=${this.inputErrors.tac}
									@value-change=${(ev) => this.setFormValue('tac', ev.detail.value)}
									@blur=${() => this.onFieldBlur('tac')}
								>
								</arc-checkbox>
							</div>
							<div class="button">
								<arc-button
									class="submitButton"
									@click=${this.onSubscribe}
									title="${msg('sepa_checkout_subscribe')}"
									type="primary"
									showIcon="true"
									iconColor="ok"
									.disabled=${!this.isValid}
									.additionalInfo="${false}"
								>
									${arrowRight}
								</arc-button>
							</div>
						</div>
					</div>
					<button
						class="closeIcon"
						@click=${(ev) => this.onClose(ev)}
						aria-label="${msg('sepa_checkout_close')}"
					>
						${close}
					</button>
				</div>
			</div>
		`;
	}

	validateForm(validateAll = false) {
		const allErrors = {};
		this.inputErrors = {};
		this.isValid = true;

		const requiredFields = ['name', 'email', 'street', 'city', 'zip', 'accountName', 'iban'];
		requiredFields.forEach((field) => {
			if (!this.formValues[field] || this.formValues[field].trim() === '') {
				allErrors[field] = [{message: 'error/required'}];
				this.isValid = false;
			}
		});

		if (this.formValues.email && !/\S+@\S+\.\S+/.test(this.formValues.email)) {
			allErrors.email = [{message: 'error/invalidEmail'}];
			this.isValid = false;
		}

		if (this.formValues.country.length < 2) {
			allErrors.country = [{message: 'error/required'}];
			this.isValid = false;
		}

		// IBAN validation (basic format check)
		if (
			this.formValues.country.length > 1 &&
			this.formValues.iban &&
			!isValidIBAN(electronicFormatIBAN(this.formValues.iban))
		) {
			allErrors.iban = [{message: 'error/invalidIBAN'}];
			this.isValid = false;
		}

		if (this.formValues.vatId && this.formValues.vatId.trim() !== '') {
			const isValidVatId = validateVatId(this.formValues.country, this.formValues.vatId);
			if (!isValidVatId) {
				allErrors.vatId = [{message: 'error/invalidVatId'}];
				this.isValid = false;
			}
		}

		if (!this.formValues.tac) {
			allErrors.tac = [{message: 'error/required'}];
			this.isValid = false;
		}

		// Only show errors for blurred fields
		this.inputErrors = {};
		Object.keys(allErrors).forEach((field) => {
			if (validateAll || this.blurredFields.has(field)) {
				this.inputErrors[field] = allErrors[field];
			}
		});

		this.requestUpdate();
		return this.isValid;
	}
}
